import React, { useEffect, useState } from "react";
import { useMutation } from "@apollo/client";
import { FormattedMessage } from "react-intl";
import { useQueryData, useToast, useAuth, useMissionRoleApplicant } from "hooks";
import {
  GET_DATA_SERVICE_APPLICANTS_COUNT,
  GET_DATA_SERVICE_INVITATIONS_COUNT,
  GET_MISSION_ROLE_APPLICANTS,
  GET_MISSION_ROLE_INVITATIONS,
  GET_MISSION_ROLE_RECOMMENDATIONS,
} from "graphql/queries";
import {
  SELECT_APPLICANT,
  INTERVIEW_APPLICANT,
  SHORTLIST_APPLICANT,
  SEND_APPLICANT_NOTIFICATION,
  ADD_MISSION_ROLE_INVITATION,
  MOVE_APPLICANT_TO_APPLIED,
} from "graphql/mutations";
import { PAGINATION_TYPE } from "components/Pagination";
import { ICON_TYPE } from "components/Icon";
import {
  APPLICANT_LIST_FILTERS_CONFIG,
  APPLICANT_TYPES,
  getTransformedFilters,
  MISSION_ROLE_APPLICANTS_PER_PAGE,
  MISSION_ROLE_STATES,
} from "constants/index";

/**
 * useApplicants
 *
 * @params {Number}   resultsPerPage
 * @params {String}   applicantState
 * @params {String}   missionId
 * @params {Array}    missionRoleIds
 * @params {String}   keyword
 * @params {String}   organizationId
 * @params {Boolean}  published
 * @params {Boolean}  skipQuery
 * @params {String}   paginationType
 * @params {Number}   currentPage
 * @params {Object}   filters
 * @params {Object}   advancedFilters
 * @params {Object}   variables
 */
export default function useApplicants({
  resultsPerPage = MISSION_ROLE_APPLICANTS_PER_PAGE,
  paginationType = PAGINATION_TYPE.classic,
  applicantState,
  missionId,
  missionRoleIds,
  organizationId,
  isMember,
  keyword,
  published,
  filters,
  advancedFilters = {},
  variables: variablesProp = {},
  skipQuery = false,
  sortKey,
  sortDirection,
  enableCountQueries = false,
} = {}) {
  const { toast } = useToast();
  const { isShowcase, isClient, isAdmin } = useAuth();
  const { confirmNotification } = useMissionRoleApplicant({ skip: true, skipCountQuery: true });

  const refetchQueries = [GET_MISSION_ROLE_APPLICANTS, GET_DATA_SERVICE_APPLICANTS_COUNT];

  const [shortlistedModalData, setShortlistedModalData] = useState({});
  const [applicantIds, setApplicantIds] = useState(null);
  const [showNotSelectedForm, setShowNotSelectedForm] = useState(false);
  const [stateFilter, setStateFilter] = useState(
    applicantState === APPLICANT_TYPES.invited ? {} : { state: applicantState }
  );

  const getQueryName = () => {
    switch (applicantState) {
      case APPLICANT_TYPES.invited:
        return GET_MISSION_ROLE_INVITATIONS;
      case APPLICANT_TYPES.recommended:
        return GET_MISSION_ROLE_RECOMMENDATIONS;
      default:
        return GET_MISSION_ROLE_APPLICANTS;
    }
  };

  const getKeyName = () => {
    switch (applicantState) {
      case APPLICANT_TYPES.invited:
        return QUERY_KEYNAMES.invitations;
      case APPLICANT_TYPES.recommended:
        return QUERY_KEYNAMES.recommendations;
      default:
        return QUERY_KEYNAMES.applicants;
    }
  };

  const [queryName, setQueryName] = useState(getQueryName);
  const [keyName, setKeyName] = useState(getKeyName);

  const [shortlistApplicant, { loading: shortlisting }] = useMutation(SHORTLIST_APPLICANT, {
    refetchQueries,
  });
  const [selectedApplicant] = useMutation(SELECT_APPLICANT, {
    refetchQueries,
  });
  const [interviewApplicant] = useMutation(INTERVIEW_APPLICANT, {
    refetchQueries,
  });
  const [inviteApplicant] = useMutation(ADD_MISSION_ROLE_INVITATION, {
    refetchQueries: [
      GET_MISSION_ROLE_INVITATIONS,
      GET_MISSION_ROLE_RECOMMENDATIONS,
      GET_DATA_SERVICE_INVITATIONS_COUNT,
    ],
  });
  const [moveApplicantToApplied] = useMutation(MOVE_APPLICANT_TO_APPLIED, {
    refetchQueries,
  });
  const [sendApplicantNotification, { loading: notificationSending }] = useMutation(SEND_APPLICANT_NOTIFICATION, {
    refetchQueries,
  });

  const getMissionRoleIds = () => (missionRoleIds || []).map((v) => v["id"] || v);

  useEffect(() => {
    switch (applicantState) {
      case APPLICANT_TYPES.invited:
        return setQueryName(GET_MISSION_ROLE_INVITATIONS), setKeyName(QUERY_KEYNAMES.invitations);
      case APPLICANT_TYPES.recommended:
        return setQueryName(GET_MISSION_ROLE_RECOMMENDATIONS), setKeyName(QUERY_KEYNAMES.recommendations);
      default:
        return setQueryName(GET_MISSION_ROLE_APPLICANTS), setKeyName(QUERY_KEYNAMES.applicants);
    }
  }, [applicantState, setQueryName, setKeyName]);

  const commonFilters = {
    missionId,
    missionRoleIds: getMissionRoleIds(),
    organizationId,
    published,
    keyword,
  };
  const filtersByState =
    applicantState === APPLICANT_TYPES.recommended
      ? commonFilters
      : {
          ...commonFilters,
          ...(applicantState !== APPLICANT_TYPES.invited && { isMember, ...stateFilter }),
        };
  const variables = {
    filters: filters ?? filtersByState,
    orderBy: { [sortKey]: sortDirection },
    ...variablesProp,
    advancedFilters: getTransformedFilters(APPLICANT_LIST_FILTERS_CONFIG, advancedFilters),
    resultFilterMetadata: Object.keys(APPLICANT_LIST_FILTERS_CONFIG),
    includeApplicationChecklist: isAdmin || isClient,
  };

  const {
    hasNextPage,
    handleNextPage,
    hasPreviousPage,
    handlePreviousPage,
    handlePageChange,
    loading,
    loadingMore,
    error,
    data,
    refetch,
  } = useQueryData({
    queryName,
    keyName,
    resultsPerPage,
    paginationType,
    variables: {
      // For showcase or client we show estimates while for admins we show application rate
      includeSellingPriceEstimates: isShowcase || isClient,
      includeMission: false,
      ...variables,
    },
    skip: skipQuery,
    nextFetchPolicy: "standby", // To avoid refetching when missionRoleApplicant query is called with different fields.
  });

  const { loading: loadingCount, data: countData } = useQueryData({
    queryName: GET_DATA_SERVICE_APPLICANTS_COUNT,
    variables: {
      states: [
        APPLICANT_TYPES.pending,
        APPLICANT_TYPES.applied,
        APPLICANT_TYPES.selected,
        APPLICANT_TYPES.unselected,
        APPLICANT_TYPES.shortlisted,
        APPLICANT_TYPES.interviewed,
      ],
      filters: omitStateFilter(variables.filters || {}),
      advancedFilters: variables.advancedFilters,
    },
    skip: !enableCountQueries,
  });

  const { loading: loadingInvitedCount, data: invitedCountData } = useQueryData({
    queryName: GET_DATA_SERVICE_INVITATIONS_COUNT,
    variables: {
      filters: omitStateFilter(variables.filters || {}),
      advancedFilters: variables.advancedFilters,
    },
    skip: !enableCountQueries,
  });

  useEffect(() => {
    setStateFilter(applicantState === APPLICANT_TYPES.invited ? {} : { state: applicantState });
  }, [applicantState]);

  const getData = () => {
    if (applicantState === APPLICANT_TYPES.recommended) {
      /** We modify the data here to get user id's and state at the root level like other tabs. */
      return missionId || missionRoleIds?.length || organizationId
        ? data &&
            data[keyName]?.nodes?.map((item) => {
              return item.id ? item : { ...item, id: item?.profile?.id, state: applicantState };
            })
        : null;
    }
    return data && data[keyName]?.nodes;
  };

  const sendNotificationForApplicant = (id) =>
    sendApplicantNotification({
      variables: { input: { id } },
      onCompleted: async () => {
        await refetch();
        toast.success(<FormattedMessage id="Missions.Applicants.Notification.SendSuccess" />);
      },
      onError: () => toast.error(<FormattedMessage id="Missions.Applicants.Notification.SendError" />),
    });

  const getAdvancedOptions = ({ id, missionRole, notification: { required, sent } = {} }, selection) => {
    const type = APPLICANT_TYPE_VALUES.indexOf(applicantState);
    const ids = selection ? selection : [id];

    if (!ids.length || type < 0) return [];
    if (missionRole?.state === MISSION_ROLE_STATES.closed) return [];

    /**
     * Sets applicant to "unselected"
     */
    const removeOption = {
      label: <FormattedMessage id="Missions.Applicants.ApplicantAdvancedOptions.LabelAddToNotSelected" />,
      icon: ICON_TYPE.trash,
      onClick: () => {
        setApplicantIds(ids);
        setShowNotSelectedForm(true);
      },
    };

    const sendNotificationOption = {
      label: <FormattedMessage id="Missions.Applicants.Notification.SendAction" />,
      icon: ICON_TYPE.email,
      onClick: () => sendNotificationForApplicant(id),
    };

    const canSendNotification = !selection && required && !sent;

    /**
     * Sets applicant to "selected"
     */
    const selectedOption = {
      label: <FormattedMessage id="Missions.Applicants.ApplicantAdvancedOptions.LabelAddToSelected" />,
      icon: ICON_TYPE.checkmark,
      onClick: () =>
        selectedApplicant({
          variables: { input: { ids } },
          onCompleted: (data) => {
            const selected = data?.selectMissionRoleApplicants?.selected || [];
            const failedToSelect = data?.selectMissionRoleApplicants?.failedToSelect || [];
            handleToastMessages(
              selected,
              failedToSelect,
              "Missions.Applicants.ApplicantAdvancedOptions.SuccessSelected",
              "Missions.Applicants.ApplicantAdvancedOptions.Error",
              "Missions.Applicants.ApplicantAdvancedOptions.PartiallySelected"
            );
          },
          onError: () => toast.error(<FormattedMessage id="Missions.Applicants.ApplicantAdvancedOptions.Error" />),
        }),
    };

    /**
     * Sets applicant to "interviewing"
     */
    const interviewingOption = {
      label: <FormattedMessage id="Missions.Applicants.ApplicantAdvancedOptions.LabelAddToInterviewing" />,
      icon: ICON_TYPE.video,
      onClick: () =>
        interviewApplicant({
          variables: { input: { ids } },
          onCompleted: (data) => {
            const interviewing = data?.interviewedMissionRoleApplicants?.interviewed || [];
            const failedToInterview = data?.interviewedMissionRoleApplicants?.failedToInterviewed || [];
            handleToastMessages(
              interviewing,
              failedToInterview,
              "Missions.Applicants.ApplicantAdvancedOptions.SuccessInterviewing",
              "Missions.Applicants.ApplicantAdvancedOptions.Error",
              "Missions.Applicants.ApplicantAdvancedOptions.PartiallyInterviewed"
            );
          },
          onError: () => toast.error(<FormattedMessage id="Missions.Applicants.ApplicantAdvancedOptions.Error" />),
        }),
    };

    /**
     * Sets applicant to "shorlisted"
     */
    const shortListOption = {
      label: <FormattedMessage id="Missions.Applicants.ApplicantAdvancedOptions.LabelAddToShortlist" />,
      icon: ICON_TYPE.plus,
      onClick: async () => {
        const shouldNotify = selection ? false : await confirmNotification(APPLICANT_TYPES.shortlisted);
        if (shouldNotify == null) return; // User cancelled notification confirmation modal

        shortlistApplicant({
          variables: { input: { ids, shouldNotify } },
          onCompleted: (data) => {
            const shortlisted = data?.shortlistMissionRoleApplicants?.shortlisted || [];
            const failedToShortlist = data?.shortlistMissionRoleApplicants?.failedToShortlist || [];
            handleToastMessages(
              shortlisted,
              failedToShortlist,
              "Missions.Applicants.ApplicantAdvancedOptions.SuccessShortlist",
              "Missions.Applicants.ApplicantAdvancedOptions.Error",
              "Missions.Applicants.ApplicantAdvancedOptions.PartiallyShortlisted"
            );
          },
          onError: () => toast.error(<FormattedMessage id="Missions.Applicants.ApplicantAdvancedOptions.Error" />),
        });
      },
    };

    /**
     * Sets applicant to "shorlisted" from "pending"
     */
    const shortListFromPendingOption = {
      label: <FormattedMessage id="Missions.Applicants.ApplicantAdvancedOptions.LabelAddToShortlist" />,
      icon: ICON_TYPE.plus,
      onClick: () => setShortlistedModalData({ show: true, id }),
    };

    /**
     * Sets applicant to "invite"
     */
    const missionInviteMember = {
      label: <FormattedMessage id="Missions.Applicants.ApplicantAdvancedOptions.LabelAddToInvite" />,
      icon: ICON_TYPE.plus,
      onClick: () =>
        inviteApplicant({
          variables: {
            input: {
              missionRoleId: missionRole?.slug,
              profileIds: ids,
            },
          },
          onCompleted: () =>
            toast.success(<FormattedMessage id="Missions.Applicants.ApplicantAdvancedOptions.SuccessInvite" />),
          onError: () =>
            toast.error(<FormattedMessage id="Missions.Applicants.ApplicantAdvancedOptions.ErrorInvite" />),
        }),
    };

    /**
     * Set applicant to "applied"
     */
    const appliedOption = {
      label: <FormattedMessage id="Missions.Applicants.ApplicantAdvancedOptions.LabelAddToApplicants" />,
      icon: ICON_TYPE.email,
      onClick: () =>
        moveApplicantToApplied({
          variables: { input: { ids } },
          onCompleted: (data) => {
            const applied = data?.moveMissionRoleApplicantsToApplied?.applied || [];
            const failedToApply = data?.moveMissionRoleApplicantsToApplied?.failedToApply || [];
            handleToastMessages(
              applied,
              failedToApply,
              "Missions.Applicants.ApplicantAdvancedOptions.SuccessApplicants",
              "Missions.Applicants.ApplicantAdvancedOptions.Error",
              "Missions.Applicants.ApplicantAdvancedOptions.PartiallyApplied"
            );
          },
          onError: () => toast.error(<FormattedMessage id="Missions.Applicants.ApplicantAdvancedOptions.Error" />),
        }),
    };

    switch (type) {
      // selected
      case 0:
        return selection
          ? []
          : [interviewingOption, removeOption, ...(canSendNotification ? [sendNotificationOption] : [])];

      // interviewing
      case 1:
        return selection
          ? [removeOption]
          : [selectedOption, shortListOption, removeOption, ...(canSendNotification ? [sendNotificationOption] : [])];

      // shortlist
      case 2:
        return selection
          ? [removeOption]
          : [interviewingOption, appliedOption, removeOption, ...(canSendNotification ? [sendNotificationOption] : [])];

      // applied
      case 3:
        return [shortListOption, removeOption, ...(canSendNotification ? [sendNotificationOption] : [])];

      // not selected
      case 4:
        return [shortListOption, ...(canSendNotification ? [sendNotificationOption] : [])];

      // Recommendation tab
      case 5:
        return [missionInviteMember];

      // pending
      case 6:
        return selection ? [] : [shortListFromPendingOption];

      // for all other tabs
      default:
        return [];
    }
  };

  const handleToastMessages = (successList, failureList, successId, errorId, partialId = null) => {
    if (successList.length && !failureList.length) {
      toast.success(<FormattedMessage id={successId} />);
    } else if (successList.length && failureList.length) {
      toast.info(
        <FormattedMessage
          id={partialId || successId}
          values={{ successCount: successList.length, failedCount: failureList.length }}
        />
      );
    } else if (!successList.length && failureList.length) {
      toast.error(<FormattedMessage id={errorId} />);
    }
  };

  const getUpdateOptionsForApplicant = (state) => VALID_STATES_FOR_UPDATE[state] || [state];

  return {
    data: getData(),
    requestedState: data?.[keyName]?.requestedState,
    filterMetadata: data?.[keyName]?.filterMetadata,
    hasNextPage,
    handleNextPage,
    hasPreviousPage,
    handlePreviousPage,
    handlePageChange,
    loading,
    loadingMore,
    error,
    refetch,
    getAdvancedOptions,
    sendNotificationForApplicant,
    notificationSending,
    getUpdateOptionsForApplicant,
    paginationType,
    resultsPerPage: data?.[keyName]?.perPage,
    showNotSelectedForm,
    setShowNotSelectedForm,
    applicantIds,
    setApplicantIds,
    handleToastMessages,
    shortlistedModalData,
    setShortlistedModalData,
    shortlistApplicant,
    shortlisting,
    loadingCount: loadingCount || loadingInvitedCount,
    count: {
      total: data?.[keyName]?.totalCount || 0,
      applied: countData?.dataServiceApplicantsCount?.applied,
      interviewing: countData?.dataServiceApplicantsCount?.interviewed,
      invited: invitedCountData?.dataServiceInvitationsCount?.count,
      selected: countData?.dataServiceApplicantsCount?.selected,
      shortlisted: countData?.dataServiceApplicantsCount?.shortlisted,
      unselected: countData?.dataServiceApplicantsCount?.unselected,
      pending: countData?.dataServiceApplicantsCount?.pending,
    },
    pageInfo: data?.[keyName]?.pageInfo,
    currentPage: data?.[keyName]?.currentPage,
  };
}

const VALID_STATES_FOR_UPDATE = {
  [APPLICANT_TYPES.pending]: [APPLICANT_TYPES.pending],
  [APPLICANT_TYPES.applied]: [APPLICANT_TYPES.applied, APPLICANT_TYPES.shortlisted, APPLICANT_TYPES.unselected],
  [APPLICANT_TYPES.shortlisted]: [
    APPLICANT_TYPES.shortlisted,
    APPLICANT_TYPES.interviewed,
    APPLICANT_TYPES.applied,
    APPLICANT_TYPES.unselected,
  ],
  [APPLICANT_TYPES.interviewed]: [
    APPLICANT_TYPES.interviewed,
    APPLICANT_TYPES.selected,
    APPLICANT_TYPES.shortlisted,
    APPLICANT_TYPES.unselected,
  ],
  [APPLICANT_TYPES.selected]: [APPLICANT_TYPES.selected, APPLICANT_TYPES.interviewed, APPLICANT_TYPES.unselected],
  [APPLICANT_TYPES.unselected]: [APPLICANT_TYPES.unselected, APPLICANT_TYPES.shortlisted],
};

// Add new values at the end. Don't change the ordering.
const APPLICANT_TYPE_VALUES = [
  APPLICANT_TYPES.selected,
  APPLICANT_TYPES.interviewed,
  APPLICANT_TYPES.shortlisted,
  APPLICANT_TYPES.applied,
  APPLICANT_TYPES.unselected,
  APPLICANT_TYPES.recommended,
  APPLICANT_TYPES.pending,
];

const QUERY_KEYNAMES = {
  applicants: "missionRoleApplicants",
  invitations: "missionRoleInvitations",
  recommendations: "missionRoleRecommendations",
};

const omitStateFilter = (filters = {}) => {
  const nextFilters = { ...filters };

  delete nextFilters.state;

  return nextFilters;
};
